import type { ChatMessage } from '../../types/admin';
import { cn } from '../../lib/utils';
import { User, Bot } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

interface ConversationTranscriptProps {
  messages: ChatMessage[];
}

function formatTimestamp(timestamp: string): string {
  try {
    return format(new Date(timestamp), 'MMM d, yyyy h:mm:ss a');
  } catch {
    return timestamp;
  }
}

export default function ConversationTranscript({
  messages,
}: ConversationTranscriptProps) {
  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No messages in this conversation
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((message, index) => {
        const isUser = message.role === 'user';

        return (
          <div
            key={index}
            className={cn(
              'flex gap-3',
              isUser ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            {/* Avatar */}
            <div
              className={cn(
                'flex-shrink-0 rounded-full p-2 h-fit',
                isUser ? 'bg-blue-100' : 'bg-gray-100'
              )}
            >
              {isUser ? (
                <User className="h-4 w-4 text-blue-600" />
              ) : (
                <Bot className="h-4 w-4 text-gray-600" />
              )}
            </div>

            {/* Message bubble */}
            <div
              className={cn(
                'flex flex-col max-w-[80%]',
                isUser ? 'items-end' : 'items-start'
              )}
            >
              <div
                className={cn(
                  'rounded-lg px-4 py-2',
                  isUser
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                )}
              >
                {isUser ? (
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                ) : (
                  <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-li:my-0">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                )}
              </div>
              <span
                className={cn(
                  'text-xs text-gray-400 mt-1',
                  isUser ? 'text-right' : 'text-left'
                )}
              >
                {formatTimestamp(message.timestamp)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
